import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorState } from '@/components/common/ErrorState';

interface ErrorBoundaryProps {
  children: ReactNode;
  // Optional side-channel for reporting caught errors to telemetry
  // (Sentry/Cloudflare/etc.). Wiring is intentionally injectable so this
  // component stays decoupled from the chosen vendor.
  // TODO: hook this up to whatever telemetry we settle on — until then,
  // production crashes here are invisible to operators.
  onError?: (error: Error, info: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  // Keeping the error on state (not just `hasError`) makes it
  // available during the fallback render — useful for surfacing a
  // dev-mode stack, an error ID, or a vendor `eventId` returned from
  // the telemetry hook in the future.
  error: Error | null;
}

// Catches render-time errors anywhere below it (Layout, header, page
// components) and renders the minimal chrome-less fallback — header and
// footer are intentionally NOT mounted here because the crash may have
// originated inside them, and re-mounting the same broken subtree would
// just throw again.
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('ErrorBoundary caught render error', error, info);
    this.props.onError?.(error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          className="min-h-screen bg-wolisho-cream flex items-center justify-center px-4"
          data-testid="error-boundary-fallback"
        >
          <ErrorState
            heading="Something went wrong"
            message="An unexpected error happened. Reloading usually fixes it."
            onPrimaryAction={this.handleReload}
            primaryActionLabel="Reload"
          />
        </div>
      );
    }
    return this.props.children;
  }
}
